/**
 * hq-bundle-builder
 *
 * Interaction model
 * -----------------
 * quantity tiers  ->  one shade / texture for the set  ->  pick N lengths out
 * of a single grid of pills.
 *
 * There is no per-bundle editor and no tabs: the number of picks IS the number
 * of bundles, a counter says how many are still needed, and the button refuses
 * to be a price until the set is complete. Tapping a chosen length again adds a
 * second bundle of it; the badge shows how many.
 *
 * Cart: one line item per bundle, sharing a _hq_bundle property, so inventory
 * is drawn down per length. The tier discount itself is applied by Kaching
 * (collection breaks) — the percentages here only draw the preview.
 *
 * HQ-BUNDLE-PRICING (JS-1) UPDATE, 2026-10 — replaces the "Cart:" note above:
 * every line of a set shares a hidden _bundle_id (and _bundle_size). The tier
 * discount is applied at checkout by the bundle-tier-discount Function from
 * the product's custom.bundle_tier_discounts metafield — the same tiers this
 * page reads — and every figure shown here comes from
 * assets/hq-bundle-pricing.js, which does the Function's arithmetic, so the
 * price on the button is the price at checkout to the cent.
 *
 * Shade control: photographic tiles when the snippet supplied a crop for every
 * value, text segments otherwise. Nothing here resolves a file name — the
 * snippet does that in Liquid, by the same rule the host section uses for the
 * gallery, and hands over a finished value -> url map.
 *
 * CHANGE IN THIS VERSION
 * ----------------------
 * The section's own Price block ([data-hq2-price]) is rendered once by the
 * section for the first available variant, and nothing ever updated it when a
 * length pill was tapped, so it sat on that first price. syncHostPrice() now
 * writes the price of the length being worked on into it on every update, and
 * announces it with an `hq:bundle:price` event for anything else on the page.
 */
class HqBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.productUrl = this.dataset.productUrl;
    this.colorIndex = parseInt(this.dataset.colorIndex, 10);
    this.textureIndex = parseInt(this.dataset.textureIndex, 10);
    this.lengthIndex = parseInt(this.dataset.lengthIndex, 10);
    this.ladderStep = Math.max(1, parseInt(this.dataset.ladderStep, 10) || 1);
    /* Which texture the set opens on. Empty when the block leaves the setting
       blank, in which case boot() keeps the old behaviour and takes the first
       value the product carries. */
    this.defaultTexture = this.dataset.defaultTexture || '';
    this.showPrice = this.dataset.showLengthPrice === 'true';
    this.cartRedirect = this.dataset.cartRedirect === 'true';

    this.el = {
      tiers: this.querySelector('[data-tiers]'),
      shadeBlock: this.querySelector('[data-shade-block]'),
      shade: this.querySelector('[data-shade]'),
      shadeCurrent: this.querySelector('[data-shade-current]'),
      textureCurrent: this.querySelector('[data-texture-current]'),
      textureBlock: this.querySelector('[data-texture-block]'),
      texture: this.querySelector('[data-texture]'),
      prompt: this.querySelector('[data-prompt]'),
      counter: this.querySelector('[data-counter]'),
      chosen: this.querySelector('[data-chosen]'),
      pills: this.querySelector('[data-pills]'),
      clear: this.querySelector('[data-clear]'),
      same: this.querySelector('[data-same]'),
      hint: this.querySelector('[data-hint]'),
      rowRetail: this.querySelector('[data-row-retail]'),
      retail: this.querySelector('[data-retail]'),
      total: this.querySelector('[data-total]'),
      save: this.querySelector('[data-save]'),
      add: this.querySelector('[data-add]'),
      addLabel: this.querySelector('[data-add-label]'),
      error: this.querySelector('[data-error]'),
    };

    // HQ-BUNDLE-PRICING start (JS-2): Connect to the shared pricing script. Added
    this.pricing = window.HqBundlePricing;
    if (!this.pricing) {
      console.error('[hq-bundle-builder] hq-bundle-pricing.js is not loaded');
      this.showError('Could not load prices. Please refresh the page.');
      this.el.add.disabled = true;
      return;
    }
    // HQ-BUNDLE-PRICING end (JS-2)
    const cfg = this.readJson('[data-config]', { tiers: [], text: {} });
    // HQ-BUNDLE-PRICING start (JS-3): Tier percentages from the metafield. Original code kept for rollback:
    // this.tiers = (cfg.tiers || []).sort((a, b) => a.qty - b.qty);
    // HQ-BUNDLE-PRICING new code (JS-3):
    this.tierSource = this.pricing.normalizeTiers(cfg.tierMap);
    this.tiers = (cfg.tiers || [])
      .map((x) => ({ qty: x.qty, badge: x.badge, percent: this.pricing.tierPercent(this.tierSource, x.qty) }))
      .sort((a, b) => a.qty - b.qty);
    // HQ-BUNDLE-PRICING end (JS-3)

    /* Placeholder strings default HERE rather than in Liquid: Liquid-C rejects
       a brace inside a quoted filter argument, so `default: 'Choose {count}…'`
       is a syntax error in the snippet. Anything the block leaves blank falls
       back to these. */
    this.t = Object.assign({
      one: 'bundle',
      many: 'bundles',
      regular: 'Regular',
      prompt: 'Choose {count} lengths',
      counter: '{n} of {total} chosen',
      chosen: 'Your set',
      empty: 'Nothing chosen yet',
      add: 'Add set to cart',
      addMore: 'Choose {count} more',
      soldOut: 'Unavailable',
      added: 'Added — {count} in your cart',
      hintRepeat: 'Tap again for a second of the same length',
      hintSame: 'Every bundle the same length',
    }, Object.fromEntries(
      Object.entries(cfg.text || {}).filter(([, v]) => v != null && v !== '')
    ));
    if (!this.tiers.length) this.tiers = [{ qty: 1, percent: 0, badge: '' }];

    this.options = this.readJson('[data-options]', {});
    this.variants = this.readJson('[data-variants]', null);

    /* Option value -> swatch crop url. Absent for a value with no crop, and
       absent altogether on a product that has none. */
    this.swatches = this.readJson('[data-swatches]', {});

    const wanted = parseInt(this.dataset.default, 10) || 1;
    this.quantity = (this.tiers.find((x) => x.qty === wanted) || this.tiers[0]).qty;

    /* The length the shopper touched last. The section's Price block follows
       it, so tapping 24" shows what a 24" bundle costs. */
    this.lastLength = null;

    this.el.add.addEventListener('click', () => this.addToCart());

    /* Add-on rows are blocks of the section, outside this element, so their
       change event is listened for on the host: ticking a kit changes what the
       button charges and the button must say so. */
       const buyHost = this.closest('hq2-pdp') || document;
this.buyNow = buyHost.querySelector('[data-hq2-buynow]');
if (this.buyNow) this.buyNow.addEventListener('click', () => this.addToCart({ checkout: true }));
    const addonHost = this.closest('hq2-pdp');
    if (addonHost) {
      addonHost.addEventListener('change', (e) => {
        if (!e.target.matches || !e.target.matches('[data-hq2-addon], [data-hqpdp-kit]')) return;
        this.update();
      });
    }
    this.el.clear.addEventListener('click', () => {
      this.picks = [];
      this.userCount = 0;
      this.lastLength = null;
      this.sameLength = false;
      this.update();
    });

    /* One length for the whole set. Turning it on collapses the picks onto the
       shortest one already chosen rather than emptying them, so the price never
       disappears mid-decision. Turning it off leaves that set in place, which
       makes it a starting point for editing bundle by bundle. */
    this.sameLength = false;
    this.el.same.addEventListener('click', () => {
      this.sameLength = !this.sameLength;
      // Old If commented:
      // if (this.sameLength) {
      //   const base = this.sorted()[0] || this.lengths[0];
      //   this.picks = new Array(this.quantity).fill(base);
      //   this.lastLength = base;
      // }
      //new If:
        if (this.sameLength) {
        const mine = this.picks.slice(0, this.userCount)
          .sort((a, b) => this.inches(a) - this.inches(b));
        const base = mine[0] || this.sorted()[0];
        if (base) {
          this.picks = new Array(this.quantity).fill(base);
          this.lastLength = base;
          this.userCount = mine.length ? this.quantity : 0;
        }
      }
      this.update();
    });

    if (this.variants) this.boot();
    else this.loadVariants();
  }

  readJson(selector, fallback) {
    const node = this.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error('[hq-bundle-builder] bad JSON in', selector, err);
      return fallback;
    }
  }

  // HQ-MULTIBUY start (BB-2): Load every variant above 250, not only the first 250. Original code kept for rollback:
  // /** Above the 250 cap the inline JSON is absent; .js returns the full set. */
  // loadVariants() {
  //   fetch(`${this.productUrl}.js`)
  // HQ-MULTIBUY new code (BB-2):
  /**
   * Above the 250 cap the inline JSON is absent. product.js stops at 250 too,
   * so the variants come from the variant feed (sections/hq-variant-feed
   * .liquid), 250 per page: [id, option1, option2, option3, price, available,
   * stock]. product.js is the fallback, as before this change.
   */
  loadVariants() {
    const expected = Math.max(1, parseInt(this.dataset.feedPages, 10) || 1);
    const count = Object.keys(this.options || {}).length || 1;
    const WAIT = [1000, 3000, 6000];
    const parse = (html) => {
      const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hq-variant-feed]');
      if (!node) throw new Error('variant feed missing');
      return JSON.parse(node.textContent);
    };
    const page = (n, attempt = 0) =>
      fetch(`${this.productUrl}?section_id=hq-variant-feed&page=${n}`, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(`variant feed ${r.status}`); return r.text(); })
        .then(parse)
        .catch((err) => {
          if (attempt >= WAIT.length) throw err;
          return new Promise((res) => setTimeout(res, WAIT[attempt])).then(() => page(n, attempt + 1));
        });
    // Two requests at a time: a burst of them is what the storefront answers with 429.
    const all = (numbers) => {
      const out = new Array(numbers.length);
      let next = 0;
      const lane = () => {
        if (next >= numbers.length) return Promise.resolve();
        const i = next;
        next += 1;
        return page(numbers[i]).then((r) => { out[i] = r; return lane(); });
      };
      return Promise.all([lane(), lane()]).then(() => out);
    };
    const range = (from, to) => { const a = []; for (let n = from; n <= to; n += 1) a.push(n); return a; };

    all(range(1, expected))
      .then((results) => {
        const total = (results[0] && results[0].pages) || expected;
        return total > expected ? all(range(expected + 1, total)).then((more) => results.concat(more)) : results;
      })
      .then((results) => {
        const rows = results.reduce((acc, r) => acc.concat((r && r.v) || []), []);
        if (!rows.length) throw new Error('variant feed empty');
        this.variants = rows.map((r) => ({
          id: r[0], o: [r[1], r[2], r[3]].slice(0, count).map(String), p: r[4], a: r[5] === true,
        }));
        this.boot();
      })
      .catch((err) => {
        console.error('[hq-bundle-builder] variant feed failed, using product.js', err);
        this.loadVariantsFromProductJs();
      });
  }

  /** The way variants were loaded before the feed: product.js, first 250 only. */
  loadVariantsFromProductJs() {
    fetch(`${this.productUrl}.js`)
  // HQ-MULTIBUY end (BB-2)
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((p) => {
        this.variants = p.variants.map((v) => ({
          id: v.id, o: v.options.map(String), p: v.price, a: v.available,
        }));
        this.boot();
      })
      .catch((err) => {
        console.error('[hq-bundle-builder] variant load failed', err);
        this.showError('Could not load options. Please refresh the page.');
      });
  }

  boot() {
    /* Prices follow the pattern Liquid's money filter printed for 12,345.67
       (symbol, its side, separators, cents or not), so a recalculated price
       looks exactly like the server-rendered ones. Intl with style 'currency'
       printed "UAH 3,396.00" beside the page's "₴3,396.00". */
    // HQ-BUNDLE-PRICING start (JS-4): Money format + currency from the shared script. Original code kept for rollback:
    // this.moneyFmt = (() => {
    //   const sample = String(this.dataset.moneySample || '');
    //   const run = sample.match(/\d[\d.,'’\s  ]*\d|\d/);
    //   if (!run) return null;
    //   const num = run[0];
    //   const dec = num.match(/[.,](\d{2})$/);
    //   const intPart = dec ? num.slice(0, -3) : num;
    //   const group = intPart.match(/[^\d]/);
    //   return {
    //     prefix: sample.slice(0, run.index),
    //     suffix: sample.slice(run.index + num.length),
    //     decSep: dec ? num.charAt(num.length - 3) : '',
    //     groupSep: group ? group[0] : '',
    //     decimals: dec ? 2 : 0,
    //   };
    // })();
    // HQ-BUNDLE-PRICING new code (JS-4):
    this.moneyFmt = this.pricing.parseMoneySample(this.dataset.moneySample);
    this.step = this.pricing.currencyStep(this.dataset.currency);
    // HQ-BUNDLE-PRICING end (JS-4)
   
    // custom code to hide decimal points
    if (this.moneyFmt) {
  this.moneyFmt.decimals = 0;
  this.moneyFmt.decSep = '';
}


    this.shades = this.colorIndex > -1 ? this.values(this.colorIndex) : [];
    this.textures = this.textureIndex > -1 ? this.values(this.textureIndex) : [];
    this.lengths = this.values(this.lengthIndex)
      .slice()
      .sort((a, b) => this.inches(a) - this.inches(b));

    this.shade = this.shades[0] || null;

    /* The texture the block asked for, matched loosely on case and surrounding
       space so "body wave" in the setting still finds "Body Wave" in the
       option. It cannot be matched exactly: the setting is typed by hand in the
       theme editor and the option value is typed by hand in the admin, and the
       two will not always agree on capitals.
     *
     * Falls back to the first value, which is what this always did. A setting
     * that matches nothing must leave the set with a texture rather than with
     * none — every variant lookup below is keyed on one. */
    const wantTexture = this.defaultTexture.trim().toLowerCase();
    this.texture = this.textures.find((v) => String(v).trim().toLowerCase() === wantTexture)
      || this.textures[0]
      || null;

    this.buildTiers();
    this.buildShades();
    this.buildSeg(this.el.texture, this.textures, (v) => { this.texture = v; this.update(); });
    if (this.shades.length > 1) this.el.shadeBlock.hidden = false;
    if (this.textures.length > 1) this.el.textureBlock.hidden = false;

    this.seed();
    this.update();

    /* The section paints its own price when it initialises, which can land
       after this first update on a cold load. Write ours again once the page
       has settled so the first thing shown is the seeded length, not the first
       available variant. */
    if (document.readyState === 'complete') {
      setTimeout(() => this.syncHostPrice(), 0);
    } else {
      window.addEventListener('load', () => this.syncHostPrice(), { once: true });
    }
  }

  /* Display only - the stored option value is left untouched for matching and
     for the cart. Same rule as snippets/hq-title-case.liquid: raise the first
     letter of each word, keep the rest as entered ("HD lace" -> "HD Lace"),
     and leave short joining words lower case unless they open the text. */
  tc(v) {
    const small = ['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'into', 'nor', 'of', 'on', 'or', 'per', 'the', 'to', 'via', 'vs', 'with'];
    return String(v == null ? '' : v).split(' ').map((w, i) => (
      i > 0 && small.includes(w.toLowerCase()) ? w : w.charAt(0).toUpperCase() + w.slice(1)
    )).join(' ');
  }

  values(i) {
    const opt = this.options[String(i)];
    return opt ? opt.values.map(String) : [];
  }

  inches(v) {
    const m = String(v).match(/\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }

  fmt(cents) {
    // HQ-BUNDLE-PRICING start (JS-5): Show cents (fixes "$203" vs $202.50). Original code kept for rollback:
    // let n = Math.round(cents || 0);
    // const neg = n < 0;
    // n = Math.abs(n);
    // const f = this.moneyFmt;
    // if (!f) return `${neg ? '-' : ''}$${(n / 100).toFixed(2)}`;
    // const parts = (f.decimals ? (n / 100).toFixed(2) : String(Math.round(n / 100))).split('.');
    // const whole = f.groupSep ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, f.groupSep) : parts[0];
    // return `${neg ? '-' : ''}${f.prefix}${whole}${f.decimals ? f.decSep + parts[1] : ''}${f.suffix}`;
    // HQ-BUNDLE-PRICING new code (JS-5):

// this is old code
    // return this.pricing.formatMoney(cents, this.moneyFmt);

// replace with updated one to hide decimals
const wholeDollars = Math.round((cents || 0) / 100) * 100;
  return this.pricing.formatMoney(wholeDollars, this.moneyFmt);



    // HQ-BUNDLE-PRICING end (JS-5)
  }

  fill(str, map) {
    return String(str || '').replace(/\{(\w+)\}/g, (_, k) => (map[k] != null ? map[k] : ''));
  }

  word(n) { return n === 1 ? (this.t.one || 'bundle') : (this.t.many || 'bundles'); }

  tierFor(q) { return this.tiers.find((x) => x.qty === q) || { qty: q, percent: 0 }; }

  // ---------------------------------------------------------------- state

  /**
   * Default set: a ladder from the SHORTEST length, one rung apart. The shopper
   * can change every pick, but an empty grid on load would make the price and
   * the button meaningless until they did.
   *
   * It used to start from the third shortest, on the reasoning that the middle
   * of the range is the commoner order. What that actually did was open every
   * listing on a longer, dearer set than the one the shopper asked to see, and
   * the price under the button was correspondingly higher than the "from" price
   * that brought them to the page. Starting at the bottom means the first
   * number they read is the one they were quoted.
   */
  // The old seed funtion is commented out:
  // seed() {
  //   const from = 0;
  //   this.picks = [];
  //   for (let i = 0; i < this.quantity; i += 1) {
  //     const at = Math.min(from + i * this.ladderStep, this.lengths.length - 1);
  //     this.picks.push(this.lengths[at]);
  //   }
  //   this.lastLength = null;
  // }
  // new seed function

  seed() {
    const open = this.lengths.filter((l) => {
      const v = this.variantFor(l);
      return v && v.a;
    });
    this.picks = (open.length ? open : this.lengths).slice(0, this.quantity);
    this.userCount = 0;
    this.lastLength = null;
  }

  countOf(length) { return this.picks.filter((x) => x === length).length; }

  sorted() { return this.picks.slice().sort((a, b) => this.inches(a) - this.inches(b)); }

  /* What one bundle costs in the tier rows: always the cheapest available
     length in the chosen shade and texture. It used to average the lengths
     already picked, so tapping a quantity (which re-seeds the picks) made the
     tier prices jump. Now they hold still, like the pack rows on the other
     templates, and the extra for a longer length is shown as "+" on its pill. */
  unitRetail() {
    /* Called from buildTiers(), before boot() has filled the lists. */
    const prices = (this.lengths || [])
      .map((l) => this.variantFor(l))
      .filter((v) => v && v.a && v.p)
      .map((v) => v.p);
    return prices.length ? Math.min.apply(null, prices) : 0;
  }

  variantFor(length) {
    const want = {};
    if (this.colorIndex > -1) want[this.colorIndex] = this.shade;
    if (this.textureIndex > -1) want[this.textureIndex] = this.texture;
    want[this.lengthIndex] = length;
    return this.variants.find((v) => Object.keys(want).every((i) => v.o[i] === want[i]));
  }

  // ---------------------------------------------------------------- build

  buildTiers() {
    this.el.tiers.textContent = '';
    this.tiers.forEach((tier) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hq-bb__tier';
      btn.setAttribute('role', 'radio');
      btn.dataset.qty = tier.qty;

      const dot = document.createElement('span');
      dot.className = 'hq-bb__radio';
      dot.setAttribute('aria-hidden', 'true');
      btn.appendChild(dot);

      const mid = document.createElement('span');
      mid.className = 'hq-bb__tier-mid';
      const name = document.createElement('span');
      name.className = 'hq-bb__tier-name';
      name.textContent = String(tier.qty);
      btn.setAttribute('aria-label', `${tier.qty} ${this.word(tier.qty)}`);
      mid.appendChild(name);
      if (tier.badge) {
        const badge = document.createElement('span');
        badge.className = 'hq-bb__badge';
        badge.textContent = tier.badge;
        mid.appendChild(badge);
      }
      btn.appendChild(mid);

      /* Right-hand column: what one bundle costs at this tier, with the
         undiscounted figure struck through beneath it. The percentage keeps
         its place next to that, so the row answers both "how much off" and
         "how much each" without the shopper doing the division. */
      const right = document.createElement('span');
      right.className = 'hq-bb__tier-right';

      const unit = document.createElement('span');
      unit.className = 'hq-bb__tier-unit';
      right.appendChild(unit);

      const sub = document.createElement('span');
      sub.className = 'hq-bb__tier-sub';

      const was = document.createElement('s');
      was.className = 'hq-bb__tier-was';
      sub.appendChild(was);

      const off = document.createElement('span');
      off.className = tier.percent > 0 ? 'hq-bb__tier-off' : 'hq-bb__tier-off is-plain';
      off.textContent = tier.percent > 0 ? `−${tier.percent}%` : (this.t.regular || 'Regular');
      sub.appendChild(off);

      right.appendChild(sub);
      btn.appendChild(right);

      btn.addEventListener('click', () => {
        if (this.quantity === tier.qty) return;
        this.quantity = tier.qty;
        // old if commented:
        // if (this.sameLength) {
        //   const base = this.sorted()[0] || this.lengths[0];
        //   this.picks = new Array(this.quantity).fill(base);
        //   this.lastLength = base;
        // } else {
        //   this.seed();
        // }
        // new if:
                const base = this.sorted()[0];
        if (this.sameLength && base) {
          this.picks = new Array(this.quantity).fill(base);
          this.lastLength = base;
          this.userCount = this.userCount > 0 ? this.quantity : 0;
        } else {
          this.seed();
        }
        this.update();
      });

  // old code
  //     this.el.tiers.appendChild(btn);
  //   });
  //   this.renderTierPrices();
  // }

  // new code

        this.el.tiers.appendChild(btn);
    });
    this.renderTierPrices();
    this.guardTierNames();
  }

  /* Kaching writes a price into the first tier's label.
     Whatever it does, put the quantity back. */
  guardTierNames() {
    const fix = () => {
      this.el.tiers.querySelectorAll('.hq-bb__tier').forEach((btn) => {
        const want = btn.dataset.qty;
        const mid = btn.querySelector('.hq-bb__tier-mid');
        if (!mid) return;
        let name = mid.querySelector('.hq-bb__tier-name');
        if (!name) {
          name = document.createElement('span');
          name.className = 'hq-bb__tier-name';
          mid.prepend(name);
        }
        if (name.textContent !== want) name.textContent = want;
        Array.from(mid.childNodes).forEach((n) => {
          if (n.nodeType === 3 && n.textContent.trim()) n.remove();
        });
      });
    };
    fix();
    if (this.tierGuard) this.tierGuard.disconnect();
    this.tierGuard = new MutationObserver(fix);
    this.tierGuard.observe(this.el.tiers, { childList: true, characterData: true, subtree: true });
  }

  // new code end

  renderTierPrices() {
    const unit = this.unitRetail();
    this.el.tiers.querySelectorAll('.hq-bb__tier').forEach((btn) => {
      const tier = this.tierFor(parseInt(btn.dataset.qty, 10));
      const unitEl = btn.querySelector('.hq-bb__tier-unit');
      const wasEl = btn.querySelector('.hq-bb__tier-was');
      if (!unitEl || !wasEl) return;
      if (!unit) {
        unitEl.textContent = '';
        wasEl.hidden = true;
        return;
      }
      // HQ-BUNDLE-PRICING start (JS-6): Per-bundle price on the tier rows. Original code kept for rollback:
      // const net = Math.round(unit * (1 - tier.percent / 100));
      // HQ-BUNDLE-PRICING new code (JS-6):
      const net = this.pricing.unitNet(unit, tier.percent, this.step);
      // HQ-BUNDLE-PRICING end (JS-6)
      unitEl.textContent = this.fmt(net);
      wasEl.textContent = this.fmt(unit);
      wasEl.hidden = tier.percent <= 0;
    });
  }

  /**
   * Shade control. Tiles when the snippet supplied a crop for every value,
   * text segments otherwise — the same fallback the container class already
   * committed to in Liquid, so the two cannot disagree.
   *
   * The decision is EVERY value, not any: a control that is half photographs
   * and half words reads as a rendering fault, and the two shapes are
   * different heights.
   */
  buildShades() {
    const root = this.el.shade;
    if (!root || this.shades.length < 2) return;

    const tiles = this.shades.length > 0
      && this.shades.every((v) => this.swatches[v]);

    if (!tiles) {
      this.buildSeg(root, this.shades, (v) => { this.shade = v; this.update(); });
      return;
    }

    root.textContent = '';
    this.shades.forEach((value) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hq-bb__sw';
      btn.setAttribute('role', 'radio');
      btn.dataset.value = value;
      /* The tile carries no visible text — the shade is named in the label row
         above it — so the accessible name and the tooltip are the only places
         it exists. */
      btn.title = value;
      btn.style.backgroundImage = `url("${value ? this.swatches[value] : ''}")`;

      const sr = document.createElement('span');
      sr.className = 'hq-bb__sr';
      sr.textContent = value;
      btn.appendChild(sr);

      /* A CSS background fires no load event, so the tile is marked blank up
         front and cleared by a probe against the same url. The probe costs no
         extra request — it resolves from the fetch the background already
         started. Without it a crop that 404s leaves a tile that looks chosen
         when it is empty. */
      btn.classList.add('is-blank');
      const probe = new Image();
      const clear = () => btn.classList.remove('is-blank');
      probe.onload = clear;
      probe.onerror = () => btn.classList.add('is-missing');
      probe.src = this.swatches[value];
      if (probe.complete) clear();

      btn.addEventListener('click', () => { this.shade = value; this.update(); });
      root.appendChild(btn);
    });
  }

  buildSeg(root, values, onPick) {
    if (!root || values.length < 2) return;
    root.textContent = '';
    values.forEach((value) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hq-bb__seg-item';
      btn.setAttribute('role', 'radio');
      btn.dataset.value = value;
      btn.textContent = this.tc(value);
      btn.addEventListener('click', () => onPick(value));
      root.appendChild(btn);
    });
  }

  // --------------------------------------------------------------- render

  update() {
    this.el.tiers.querySelectorAll('.hq-bb__tier').forEach((b) => {
      const on = parseInt(b.dataset.qty, 10) === this.quantity;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    this.renderTierPrices();
    this.syncSeg(this.el.shade, this.shade);
    this.syncSeg(this.el.texture, this.texture);
    this.syncHostShade();
    this.syncHostTexture();
    if (this.el.shadeCurrent) this.el.shadeCurrent.textContent = this.shades.length > 1 ? this.tc(this.shade) : '';
    if (this.el.textureCurrent) this.el.textureCurrent.textContent = this.textures.length > 1 ? this.tc(this.texture) : '';

    const n = this.picks.length;
    const full = n >= this.quantity;

    this.el.prompt.textContent = this.fill(this.t.prompt, { count: this.quantity });
    this.el.counter.textContent = this.fill(this.t.counter, { n, total: this.quantity });
    this.el.counter.classList.toggle('is-short', !full);

    const set = this.sorted();
    /* The chosen set reads as the value of the LENGTH label, the way the
       extension PDPs name the chosen option, so it carries no prefix. */
    this.el.chosen.textContent = set.length ? set.join(' · ') : '';

    this.el.same.setAttribute('aria-pressed', this.sameLength ? 'true' : 'false');
    this.el.same.classList.toggle('is-on', this.sameLength);
    this.el.hint.textContent = this.sameLength
      ? (this.t.hintSame || '')
      : (this.t.hintRepeat || '');

    this.renderPills(full);
    this.renderTotals(set, full);
    this.syncHostPrice();
  }

  /* Selects on [data-value] rather than on a class, so the same call syncs a
     row of text segments and a grid of tiles. */
  syncSeg(root, value) {
    if (!root) return;
    root.querySelectorAll('[data-value]').forEach((b) => {
      const on = b.dataset.value === value;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  renderPills(full) {
    this.el.pills.textContent = '';

    /* The pills quote the DIFFERENCE from the cheapest length, the way the wig
       and extension PDPs do (.hq2-pill-delta), not the full price. Fifteen full
       prices read as fifteen products; what the shopper is actually choosing
       between is what one length costs over another. The cheapest shows
       nothing — "+0" is noise. */
    const base = this.lengths
      .map((l) => this.variantFor(l))
      .filter((v) => v && v.a && v.p)
      .reduce((low, v) => (low == null || v.p < low ? v.p : low), null);

    this.lengths.forEach((length) => {
      const variant = this.variantFor(length);
      const count = this.countOf(length);
      const out = !variant || !variant.a;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hq-bb__pill';
      btn.classList.toggle('is-on', count > 0);
      btn.setAttribute('aria-pressed', count > 0 ? 'true' : 'false');

      const label = document.createElement('span');
      label.className = 'hq-bb__pill-n';
      label.textContent = length;
      btn.appendChild(label);

      if (out) {
        btn.classList.add('is-out');
        btn.disabled = true;
      } else if (this.showPrice && base != null && variant.p > base) {
        const price = document.createElement('span');
        price.className = 'hq-bb__pill-p';
        price.textContent = `+${this.fmt(variant.p - base)}`;
        btn.appendChild(price);
      }

      if (count > 0) {
        const badge = document.createElement('span');
        badge.className = 'hq-bb__pill-count';
        badge.textContent = count;
        btn.appendChild(badge);
      }

      /* Nothing on this grid is ever inert. Below the target the tap adds;
         at the target a chosen pill gives one back, and an unchosen one
         replaces the most recent pick — which is the one the shopper is most
         likely to be reconsidering.

         Once the set is full the tap can no longer add, and there it clears
         this length outright rather than giving one back: someone tapping 24"
         a fourth time is undoing that choice, not asking for three of it. On a
         set of two that means the third tap on one length empties the grid. */
      // old btn function
      // btn.addEventListener('click', () => {
      //   this.lastLength = length;
      //   if (this.sameLength) {
      //     this.picks = new Array(this.quantity).fill(length);
      //   } else if (!full) {
      //     this.picks.push(length);
      //   } else if (count > 0) {
      //     this.picks = this.picks.filter((x) => x !== length);
      //     this.lastLength = null;
      //   } else {
      //     this.picks[this.picks.length - 1] = length;
      //   }
      //   this.update();
      // });

            btn.addEventListener('click', () => {
        this.lastLength = length;
        if (this.sameLength) {
          this.picks = new Array(this.quantity).fill(length);
          this.userCount = this.quantity;
        } else if (this.userCount < this.picks.length) {
          /* Defaults still in the set: this tap takes the next default slot.
             If the tapped length is itself one of the remaining defaults,
             claim it instead of adding a duplicate. */
          const slot = this.userCount;
          const dup = this.picks.indexOf(length, slot);
          if (dup > -1) {
            [this.picks[slot], this.picks[dup]] = [this.picks[dup], this.picks[slot]];
          } else {
            this.picks[slot] = length;
          }
          this.userCount += 1;
        } else if (!full) {
          this.picks.push(length);
          this.userCount = this.picks.length;
        } else if (count > 0) {
          this.picks = this.picks.filter((x) => x !== length);
          this.userCount = this.picks.length;
          this.lastLength = null;
        } else {
          // Set is full: drop the oldest pick, add the new one as the newest
          this.picks.shift();
          this.picks.push(length);
        }
        this.update();
      });

      this.el.pills.appendChild(btn);
    });
  }

  /**
   * What the ticked add-ons come to, in cents.
   *
   * They are not discounted by the bundle tier — a kit costs what it costs —
   * so the figure is added to BOTH sides of the comparison: to what the set
   * would cost bought separately and to what is being charged. The saving in
   * between stays the bundle's own saving, which is the only honest reading.
   */
  addonTotal() {
    const host = this.closest('hq2-pdp') || document;
    let sum = 0;
    host.querySelectorAll('[data-hq2-addon], [data-hqpdp-kit]').forEach((box) => {
      if (!box.checked) return;
      sum += parseInt(box.dataset.hqpdpKitPrice || box.dataset.hq2AddonPrice, 10) || 0;
    });
    return sum;
  }

  renderTotals(set, full) {
    const resolved = set.map((l) => this.variantFor(l));
    const broken = resolved.some((v) => !v || !v.a);
    const tier = this.tierFor(this.quantity);

    if (!full || broken) {
      this.el.rowRetail.hidden = true;
      this.el.save.hidden = true;
      this.el.total.textContent = '—';
      this.el.add.disabled = true;
      this.el.addLabel.textContent = broken
        ? (this.t.soldOut || 'Unavailable')
        : this.fill(this.t.addMore, { count: this.quantity - this.picks.length });
      this.syncHostChrome(null, '', false);
      return;
    }

    const addons = this.addonTotal();
    // HQ-BUNDLE-PRICING start (JS-7): "Your price" = checkout price. Original code kept for rollback:
    // const retail = resolved.reduce((sum, v) => sum + v.p, 0) + addons;
    // const total = Math.round((retail - addons) * (1 - tier.percent / 100)) + addons;
    // HQ-BUNDLE-PRICING new code (JS-7):
    const priced = this.pricing.priceSet(resolved.map((v) => ({ subtotalCents: v.p, quantity: 1 })), this.tierSource, this.step);
    const retail = priced.subtotalCents + addons;
    const total = priced.totalCents + addons;
    // HQ-BUNDLE-PRICING end (JS-7)
    const saved = retail - total;

    this.el.total.textContent = this.fmt(total);
    this.el.rowRetail.hidden = saved <= 0;
    this.el.retail.textContent = this.fmt(retail);
    this.el.save.hidden = saved <= 0;
    if (saved > 0) this.el.save.textContent = `You save ${this.fmt(saved)} · ${tier.percent}% off`;

    this.el.add.disabled = false;
    this.el.addLabel.textContent = `${this.t.add || 'Add set to cart'} · ${this.fmt(total)}`;
    this.syncHostChrome(this.fmt(total), this.el.chosen.textContent, true);
  }

  /**
   * The section's own Price block.
   *
   * The section prints [data-hq2-price] once, for the first available variant,
   * and with the variant picker removed on bundle listings nothing ever asks it
   * to print again. That is the number that sat still while the pills changed.
   *
   * It is written here with the price of ONE bundle of the length being worked
   * on: the last one tapped, else the shortest in the set, after the tier
   * percent so it agrees with the tier rows. To show the undiscounted price
   * instead, replace `net` with `variant.p` below.
   *
   * An element that holds child markup (a "from" label, a compare-at span) has
   * only its first text node replaced, so that markup survives.
   */
  syncHostPrice() {
    if (!this.lengths || !this.variants) return;

    const inSet = this.lastLength && this.picks.indexOf(this.lastLength) > -1;
    const length = inSet ? this.lastLength : (this.sorted()[0] || this.lengths[0]);
    const variant = length ? this.variantFor(length) : null;
    if (!variant || !variant.p) return;

    const tier = this.tierFor(this.quantity);
    // HQ-BUNDLE-PRICING start (JS-8): Price block on the page. Original code kept for rollback:
    // const net = Math.round(variant.p * (1 - (tier.percent || 0) / 100));
    // HQ-BUNDLE-PRICING new code (JS-8):
    const net = this.pricing.unitNet(variant.p, tier.percent || 0, this.step);
    // HQ-BUNDLE-PRICING end (JS-8)
    const text = this.fmt(net);

    const root = this.closest('hq2-pdp') || document;
    root.querySelectorAll('[data-hq2-price]').forEach((node) => {
      if (!node.children.length) {
        if (node.textContent !== text) node.textContent = text;
        return;
      }
      const textNode = Array.from(node.childNodes)
        .find((n) => n.nodeType === 3 && n.textContent.trim());
      if (textNode && textNode.textContent.trim() !== text) textNode.textContent = text;
    });

    const key = `${variant.id}:${net}`;
    if (key === this.lastPriceKey) return;
    this.lastPriceKey = key;
    document.dispatchEvent(new CustomEvent('hq:bundle:price', {
      detail: {
        length,
        variantId: variant.id,
        price: variant.p,
        net,
        percent: tier.percent || 0,
        formatted: text,
      },
    }));
  }

  /**
   * Tell the host section which shade is chosen, so it can filter its gallery.
   *
   * The section recomputes the filter on a [data-hq2-value] click, and on this
   * template the variant picker is removed — there are none. Left to itself it
   * filtered once at load and then froze, so every shade after the first showed
   * the first shade's photographs.
   *
   * Guarded on the previous value because update() runs on every length tap as
   * well, and re-filtering an unchanged gallery would scroll the stage back to
   * its first slide under the shopper's finger.
   */
  syncHostShade() {
    if (this.shade == null || this.shade === this.lastHostShade) return;

    const host = this.closest('hq2-pdp');
    if (!host) return;

    /* This script is deferred and the section defines its element in an inline
       script, so on a cold load the custom element may not have upgraded yet
       and the method would not exist. Waiting for the definition costs nothing
       and keeps the very first filter pass, which is the one that sets the
       gallery up. */
    if (typeof host.filterByShade !== 'function') {
      customElements.whenDefined('hq2-pdp').then(() => this.syncHostShade());
      return;
    }

    this.lastHostShade = this.shade;
    host.filterByShade(this.shade);
  }

  /**
   * The same for the texture, so the gallery follows both halves of the choice.
   *
   * Separate from syncHostShade rather than folded into it: the two change
   * independently, and a single guard on either one would skip the call that
   * carried the other. Same deferred-upgrade wait, same guard against
   * re-filtering an unchanged value on every length tap.
   *
   * filterByTexture is absent on an older copy of the section, so the call is
   * guarded by name — the shade filter then keeps working on its own rather
   * than the whole update throwing.
   */
  syncHostTexture() {
    if (this.texture == null || this.texture === this.lastHostTexture) return;

    const host = this.closest('hq2-pdp');
    if (!host) return;

    if (typeof host.filterByTexture !== 'function') {
      if (customElements.get('hq2-pdp')) return;
      customElements.whenDefined('hq2-pdp').then(() => this.syncHostTexture());
      return;
    }

    this.lastHostTexture = this.texture;
    host.filterByTexture(this.texture);
  }

  /**
   * The host section's sticky bar quotes [data-hq2-price] — the price of ONE
   * bundle, because the Price block is still per-bundle and labelled as such.
   * Its button adds the whole set, so the set total is written into the bar
   * instead. The section never fights back: with the variant picker removed
   * there are no [data-hq2-value] buttons, so its render() runs once at init.
   */
  syncHostChrome(totalText, recapText, available) {
    const root = this.closest('hq2-pdp') || document;

    root.querySelectorAll('[data-hq2-sticky-price], [data-hq2-sticky-btn-price]')
      .forEach((el) => { if (totalText) el.textContent = totalText; });

    const meta = root.querySelector('[data-hq2-sticky-meta]');
    if (meta && recapText != null) meta.textContent = recapText;

    const btn = root.querySelector('[data-hq2-sticky-atc]');
    if (btn) btn.disabled = !available;

    // A price beside a disabled button reads as a promise.
    root.querySelectorAll('[data-hq2-sticky-btn-price], .hq2-sticky-btn-sep')
      .forEach((el) => { el.hidden = !available; });

    /* The set's own "Buy it now" button, when the section prints one: same
       rule as the sticky button. (Added 2026-10-16 in renderTotals, where
       root / available / totalText do not exist; moved here 2026-10-18.) */
    const buy = root.querySelector('[data-hq2-buynow]');
    if (buy) {
      buy.disabled = !available;
      const bp = buy.querySelector('[data-hq2-buynow-price]');
      const bs = buy.querySelector('[data-hq2-buynow-sep]');
      if (bp) { bp.hidden = !available; if (totalText) bp.textContent = totalText; }
      if (bs) bs.hidden = !available;
    }
  }

  // ----------------------------------------------------------------- cart

  getItems() {
    const set = this.sorted();
    if (set.length < this.quantity) return null;
    const resolved = set.map((l) => this.variantFor(l));
    if (resolved.some((v) => !v || !v.a)) return null;

    // HQ-BUNDLE-PRICING start (JS-9): What "Add set to cart" sends. Original code kept for rollback:
    // const key = `HQ-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    // const label = `${this.quantity}-${this.word(this.quantity)} set`;
    //
    // /* Identical picks must collapse into one line: Shopify merges matching
    //    variant+property lines anyway, and an un-merged payload makes the
    //    quantities drift away from the picks. */
    // const grouped = new Map();
    // resolved.forEach((v) => {
    //   const hit = grouped.get(v.id);
    //   if (hit) hit.quantity += 1;
    //   else grouped.set(v.id, { id: v.id, quantity: 1, properties: { _hq_bundle: key, Set: label } });
    // });
    // const items = Array.from(grouped.values());
    //
    // /* Add-on kits live in their own blocks on the page, outside this element,
    //    and the set is added by OUR button — so if they are not collected here
    //    a ticked kit is silently dropped. They carry the bundle key too, so the
    //    cart shows what they were bought with. */
    // const host = this.closest('hq2-pdp') || document;
    // host.querySelectorAll('[data-hq2-addon], [data-hqpdp-kit]').forEach((box) => {
    //   if (!box.checked) return;
    //   const addonId = parseInt(box.dataset.hq2Addon || box.dataset.hqpdpKit, 10);
    //   if (addonId) items.push({ id: addonId, quantity: 1, properties: { _hq_bundle: key } });
    // });
    // HQ-BUNDLE-PRICING new code (JS-9):
    const key = this.pricing.makeBundleId();
    const label = `${this.quantity}-${this.word(this.quantity)} set`;

    /* Add-on kits live in their own blocks on the page, outside this element,
       and the set is added by OUR button — so if they are not collected here
       a ticked kit is silently dropped. They are linked to the set with
       _bundle_addon_for, never _bundle_id, so they are not counted in it.
       Identical picks collapse into one line (buildCartItems). */
    const host = this.closest('hq2-pdp') || document;
    const addonIds = [];
    host.querySelectorAll('[data-hq2-addon], [data-hqpdp-kit]').forEach((box) => {
      if (!box.checked) return;
      const addonId = parseInt(box.dataset.hq2Addon || box.dataset.hqpdpKit, 10);
      if (addonId) addonIds.push(addonId);
    });

    const items = this.pricing.buildCartItems({
      variantIds: resolved.map((v) => v.id),
      bundleId: key,
      label,
      addonIds,
    });
    // HQ-BUNDLE-PRICING end (JS-9)

    return { key, items };
  }

  /** opts.checkout: go straight to checkout after adding (the "Buy it now" button). */
  addToCart(opts = {}) {
    const payload = this.getItems();
    if (!payload) return;

    this.el.add.disabled = true;
    this.el.addLabel.textContent = 'Adding…';

    /* The count bubble does not refresh on its own. Dawn re-renders it from the
       add response, so the request has to ask for those sections BY NAME. */
    const cartEl = document.querySelector('cart-drawer, cart-notification');
    const sections = cartEl && cartEl.getSectionsToRender
      ? cartEl.getSectionsToRender().map((x) => x.section || x.id)
      : ['cart-icon-bubble'];

    fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: payload.items, sections, sections_url: window.location.pathname }),
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || data.message || 'Add to cart failed');
        if (opts.checkout) { window.location.href = '/checkout'; return; }
        if (this.cartRedirect) { window.location.href = '/cart'; return; }

        /* Dawn's cart-notification renders ONE line item: its section is built
           for a single variant and found with [id="cart-notification-product-
           <variant>"]. A set adds several at once, so whichever id is handed
           over matches at most one of them — and none at all when the cart
           already held something, which is where the null came from. The
           notification is therefore only used for a single-line add. */
        const multi = payload.items.length > 1 || payload.items.some((it) => it.quantity > 1);
        /* /cart/add.js answers an items list with { items, sections } and no
           top-level key, and Dawn's popup finds its product row ONLY by
           parsedState.key. Setting just the id left the lookup on
           "cart-notification-product-undefined": the render threw, and the
           fallback below then opened the popup un-rendered — an empty product
           row and "View cart" with no count. */
        if (data && data.items && data.items[0]) {
          if (data.id == null) data.id = data.items[0].id || data.items[0].variant_id;
          if (data.key == null) data.key = data.items[0].key;
        }

        let handled = false;
        if (!multi && cartEl && cartEl.renderContents) {
          try { cartEl.renderContents(data); handled = true; }
          catch (err) { console.warn('[hq-bundle-builder] theme cart render failed', err); }
        }
        /* Never open the popup without rendering it — that is the empty box.
           If the render failed, refresh the bubble and confirm in place, the
           same way a multi-line set is confirmed. */
        if (!handled) this.refreshCartChrome(null);
        if (multi || !handled) this.confirm(payload.items.reduce((sum, it) => sum + it.quantity, 0));

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hq-bundle-builder', cartData: data });
        }
        document.dispatchEvent(new CustomEvent('hq:bundle:added', { detail: payload }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        this.update();
      })
      .catch((err) => {
        console.error('[hq-bundle-builder]', err);
        this.showError(err.message);
        this.update();
      });
  }

  /**
   * Only the count bubble is swapped by hand — it is a number with no behaviour
   * attached. The drawer is asked to open rather than having its HTML
   * overwritten: a hand-swapped drawer loses every listener inside it.
   */
  refreshCartChrome(cartEl) {
    fetch('/?sections=cart-icon-bubble')
      .then((r) => r.json())
      .then((sections) => {
        const html = sections && sections['cart-icon-bubble'];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('.shopify-section') || doc.body;
        const target = document.getElementById('cart-icon-bubble');
        if (target && fresh) target.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});

    if (cartEl && typeof cartEl.open === 'function') {
      try { cartEl.open(); } catch (err) { /* cart page still has it */ }
    }
  }

  /** In-place confirmation, since the theme's notification is skipped on sets. */
  confirm(count) {
    const el = this.el.error;
    el.hidden = false;
    el.classList.add('is-ok');
    el.textContent = this.fill(this.t.added, { count: `${count} ${this.word(count)}` });
    clearTimeout(this.confirmTimer);
    this.confirmTimer = setTimeout(() => {
      el.hidden = true;
      el.classList.remove('is-ok');
    }, 5000);
  }

  showError(message) {
    this.el.error.classList.remove('is-ok');
    this.el.error.hidden = false;
    this.el.error.textContent = message;
    setTimeout(() => { this.el.error.hidden = true; }, 6000);
  }
}

if (!customElements.get('hq-bundle-builder')) {
  customElements.define('hq-bundle-builder', HqBundleBuilder);
}

/* The section's mobile sticky bar has no variant of its own to submit, so it
   delegates here instead of posting the product form. */
document.addEventListener('hq:bundle:request-add', () => {
  const el = document.querySelector('hq-bundle-builder');
  if (el) el.addToCart();
});
